/**
 * Existing Debt Page - Frontend Workflow Implementation
 * Conditional page for existing debt input, shown only if "Has existing debt" checkbox set.
 * Supports multiple debt projects with tab-based interface.
 * Part of Frontend Workflow - Issue 5 (SPJ-140)
 *
 * Refactored to match Project Setup page patterns:
 * - Uses SidebarProvider and WorkflowStatusSidebar
 * - Uses WorkflowNavigationButtons component
 * - Uses SectionCard for debt project input
 * - Uses useWorkflowStatus hook
 * - Extracts handlers to useExistingDebtHandlers hook
 */

import { Button, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { ArrowLeft, Minus, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createEmptyExistingDebtInputForm,
  ExistingDebtInputs,
} from '~/builders/existing-debt/ExistingDebtInputs';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { ErrorFallback } from '~/components/ErrorBoundary/ErrorFallback';
import { PageLayoutGrid } from '~/components/layout/page-utilities/PageLayoutGrid';
import { WorkflowStatusSidebar } from '~/components/layout/page-utilities/WorkflowStatusSidebar';
import { useAppHeader } from '~/components/layout/shared/AppHeaderContext';
import { SidebarProvider } from '~/components/layout/shared/SidebarContext';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { SectionCard } from '~/components/project';
import { useExistingDebtHandlers } from '~/hooks/useExistingDebtHandlers';
import { useProjectStore } from '~/hooks/useProjectStore';
import { useWorkflowStatus } from '~/hooks/useWorkflowStatus';
import { DebtProjectTabs } from '~/pages/project/ExistingDebtPage/DebtProjectTabs';
import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';
import type { ExistingDebtInputForm } from '~/types';

const ExistingDebtPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setHeader } = useAppHeader();

  // Track validation display state
  const [showValidation, setShowValidation] = useState(false);

  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    debt: true, // Debt section starts expanded
  });

  const [activeTab, setActiveTab] = React.useState(0);

  // Get existing data
  const existingDebtInputs = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.existingDebtInputs : undefined
  );
  const dateRecords = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.dateRecords : undefined
  );
  const dateInputs = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.dateInputs : undefined
  );

  // Get workflow statuses from hook (single source of truth)
  const sectionStatuses = useWorkflowStatus(projectId || '');

  // Get handlers from hook
  const handlers = useExistingDebtHandlers(projectId || '');

  // Multi-project state - sync with store to ensure consistency
  // Initialize with stored data or one empty project, then sync with store updates
  const [debtProjects, setDebtProjects] = React.useState<ExistingDebtInputForm[]>(() => {
    if (existingDebtInputs && existingDebtInputs.length > 0) {
      return existingDebtInputs;
    }
    return [createEmptyExistingDebtInputForm()];
  });

  // Track when we're actively editing to prevent external sync from stealing focus
  // This ref is set during local updates and cleared after a short delay
  const isLocalEditingRef = useRef(false);
  const localEditTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mark that we're doing a local edit (prevents sync effect from overwriting)
  const markLocalEdit = useCallback(() => {
    isLocalEditingRef.current = true;
    // Clear any existing timeout
    if (localEditTimeoutRef.current) {
      clearTimeout(localEditTimeoutRef.current);
    }
    // Reset after a short delay (allows sync to happen after user stops editing)
    localEditTimeoutRef.current = setTimeout(() => {
      isLocalEditingRef.current = false;
    }, 500);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (localEditTimeoutRef.current) {
        clearTimeout(localEditTimeoutRef.current);
      }
    };
  }, []);

  // Sync local state with store when store updates externally
  // Skip sync if we're in the middle of local editing to prevent focus loss
  React.useEffect(() => {
    // Don't sync during local edits - prevents focus loss when typing
    if (isLocalEditingRef.current) {
      return;
    }
    if (existingDebtInputs && existingDebtInputs.length > 0) {
      setDebtProjects(existingDebtInputs);
    }
  }, [existingDebtInputs]);

  // Calculate completion status - must be before early returns
  const completionStatus = useMemo(() => {
    if (!projectId) {
      return {
        completeCount: 0,
        totalCount: 0,
        allComplete: false,
        debtStatus: undefined,
      };
    }
    const completeCount = debtProjects.filter(
      (p) => p.id?.trim() && p.name?.trim() && p.payments?.length > 0
    ).length;
    const allComplete =
      debtProjects.length > 0 &&
      debtProjects.every((p) => p.id?.trim() && p.name?.trim() && p.payments?.length > 0);
    const debtStatus = sectionStatuses.debt;

    return {
      completeCount,
      totalCount: debtProjects.length,
      allComplete,
      debtStatus,
    };
  }, [debtProjects, sectionStatuses.debt, projectId]);

  // Navigation handlers - wrapped in useCallback to prevent infinite loops
  // MUST be before early return to satisfy React hooks rules
  const handleBack = useCallback(() => {
    if (!projectId) return;
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.SETUP}`);
  }, [navigate, projectId]);

  const handleSkip = useCallback(() => {
    if (!projectId) return;
    handlers.handleSkip();
    // Navigate to proposal
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.PROPOSAL}`);
  }, [handlers, navigate, projectId]);

  const handleContinue = useCallback(() => {
    if (!projectId) return;
    if (!completionStatus.allComplete) {
      setShowValidation(true);
      return;
    }

    // Build cumulative debt at save point
    const result = handlers.handleSaveAndContinue();

    if (!result) {
      console.error('[ExistingDebt] Failed to build cumulative debt');
      // Could show error toast here
      return;
    }

    handlers.handleContinue();
    navigate(`/projects/${projectId}/${PROJECT_ROUTES.PROPOSAL}`);
  }, [completionStatus.allComplete, handlers, navigate, projectId]);

  // Handle project change - saves form inputs only
  const handleProjectChange = useCallback(
    (updatedProject: ExistingDebtInputForm) => {
      // Mark that we're doing a local edit to prevent sync from stealing focus
      markLocalEdit();

      // Update local state
      const updated = [...debtProjects];
      updated[activeTab] = updatedProject;
      setDebtProjects(updated);

      // Use handler from hook (saves form data only)
      handlers.handleProjectChange(updatedProject, updated, activeTab);
    },
    [debtProjects, activeTab, handlers, markLocalEdit]
  );

  // Handler to decrease count - removes one project
  const handleDecreaseCount = () => {
    const result = handlers.handleDecreaseCount(debtProjects, activeTab);
    setDebtProjects(result.updated);
    setActiveTab(result.newActiveTab);
  };

  // Handler to increase count - adds one new project
  const handleIncreaseCount = () => {
    const updated = handlers.handleIncreaseCount(debtProjects);
    setDebtProjects(updated);
    // Switch to the new tab
    setActiveTab(updated.length - 1);
  };

  // Extract primitive values for header config dependencies
  // CRITICAL: Extract primitives BEFORE using in useEffect to prevent infinite loops
  const allComplete = completionStatus.allComplete;

  // Use ref to track previous values and prevent unnecessary updates
  const prevAllCompleteRef = useRef<boolean | undefined>(undefined);

  // Set header configuration - only update when primitive values actually change
  useEffect(() => {
    // Skip if value hasn't actually changed (but always run on first render)
    if (prevAllCompleteRef.current === allComplete && prevAllCompleteRef.current !== undefined) {
      return;
    }

    prevAllCompleteRef.current = allComplete;

    setHeader({
      title: 'Existing Debt',
      leftActions: (
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Project Setup
        </Button>
      ),
      rightActions: (
        <>
          {completionStatus.completeCount === 0 ? (
            // No debt data entered - show "Skip Debt" to proceed without debt
            <Button variant="outline" size="sm" onClick={handleSkip}>
              Skip
            </Button>
          ) : null}
          <Button
            variant="solid"
            colorPalette="blue"
            size="sm"
            onClick={handleContinue}
            disabled={!allComplete && completionStatus.completeCount > 0}
          >
            Proposal Builder
          </Button>
        </>
      ),
    });
  }, [
    setHeader,
    handleBack,
    handleSkip,
    handleContinue,
    allComplete,
    completionStatus.completeCount,
  ]);

  return (
    <SidebarProvider>
      <PageLayoutGrid rightSidebar={<WorkflowStatusSidebar projectId={projectId || ''} />}>
        <VStack
          maxW={LAYOUT_SPACING.MAX_WIDTH_CONTENT}
          mx="auto"
          p={LAYOUT_SPACING.PAGE_PADDING}
          gap={LAYOUT_SPACING.SECTION_GAP}
          align="stretch"
        >
          {/* Bond Series Section - Single Row */}
          <HStack justify="space-between" mb={3} align="center">
            {/* Left: Bond tabs */}
            <HStack gap={2} flex={1} overflowX="auto">
              <DebtProjectTabs
                projects={debtProjects}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </HStack>

            {/* Right: Count + Add */}
            <HStack gap={2} align="center">
              <Text fontSize="sm" color="gray.600">
                {debtProjects.length} {debtProjects.length === 1 ? 'series' : 'series'}
              </Text>
              <IconButton
                aria-label="Decrease debt project count"
                size="sm"
                variant="outline"
                onClick={handleDecreaseCount}
                disabled={debtProjects.length <= 1}
              >
                <Minus size={18} />
              </IconButton>
              <IconButton
                aria-label="Increase debt project count"
                size="sm"
                variant="outline"
                onClick={handleIncreaseCount}
              >
                <Plus size={18} />
              </IconButton>
            </HStack>
          </HStack>

          {/* Active Debt Project Input Section */}
          <SectionCard
            title={debtProjects[activeTab]?.name?.trim() ?? `Debt Project ${activeTab + 1}`}
            description="Configure bond series details, payment schedule, and interest rates"
            status={completionStatus.debtStatus}
            isRequired
            isDisabled={!dateRecords}
            showValidation={showValidation}
            isExpanded={expandedSections.debt}
            onExpandedChange={(expanded) =>
              setExpandedSections((prev) => ({ ...prev, debt: expanded }))
            }
          >
            <ErrorBoundary fallback={<ErrorFallback section="existing debt" />}>
              <ExistingDebtInputs
                values={debtProjects[activeTab]}
                onUpdate={handleProjectChange}
                availablePaymentDates={dateRecords || []}
                dateInputs={dateInputs || undefined}
                isDisabled={!dateRecords}
              />
            </ErrorBoundary>
          </SectionCard>
        </VStack>
      </PageLayoutGrid>
    </SidebarProvider>
  );
};

export default ExistingDebtPage;
